import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { PageHeader } from "@/components/DashboardShell";
import { StatusPill, Stat, PrimaryButton } from "@/components/brand";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";

export default function MerchantOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(null);

  const mStatus = user?.merchant_status ?? "approved";
  const approved = mStatus === "approved";

  const load = useCallback(async () => {
    if (mStatus !== "approved") { setTotal(0); return; }
    const [s, d] = await Promise.all([api.get("/stats/merchant"), api.get("/deliveries")]);
    setStats(s.data);
    setOrders(d.data.slice(0, 6));
    setTotal(d.data.length);
  }, [mStatus]);
  useEffect(() => { load(); }, [load]);

  const banner = !approved && (
    <div data-testid="merchant-status-banner" className={`mb-8 border px-5 py-4 ${mStatus === "rejected" ? "border-orange-500 bg-orange-50" : "border-gray-300 bg-gray-50"}`}>
      <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500 mb-1">
        {mStatus === "rejected" ? "Account not approved" : "Pending approval"}
      </div>
      <div className="text-sm text-[#0a0a0a]">
        {mStatus === "rejected"
          ? "Your merchant account was not approved. Please contact our team if you believe this is a mistake."
          : "Your Routa merchant account is currently under review. You'll be notified once your account has been approved, after which you can start creating deliveries."}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        index="01" eyebrow="Merchant Overview" title="Dispatch overview"
        actions={approved && <PrimaryButton as={Link} to="/merchant/create" orange arrow data-testid="overview-create-button"><PlusCircle className="h-4 w-4" /> New delivery</PrimaryButton>}
      />

      {banner}

      {approved && (
        <>
          {user && total !== null && <OnboardingChecklist userId={user.id} orderCount={total} />}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 border border-gray-200 p-6 mb-10">
            <Stat label="Active orders" value={stats?.active ?? "—"} />
            <Stat label="Delivered" value={stats?.delivered ?? "—"} />
            <Stat label="On-time rate" value={stats ? `${stats.on_time_rate}%` : "—"} />
            <Stat label="Total spend" value={stats ? `$${stats.spend.toFixed(2)}` : "—"} />
          </div>

          <div className="flex items-center gap-4 mb-4">
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Recent orders</span>
            <span className="h-px flex-1 bg-gray-200" />
            <Link to="/merchant/orders" className="font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 hover:underline">View all</Link>
          </div>

          <div className="border border-gray-200 divide-y divide-gray-200">
            {orders.length === 0 && <div className="px-5 py-10 text-center text-sm text-gray-400">No orders yet. Create your first delivery.</div>}
            {orders.map((d) => (
              <Link key={d.id} to={`/merchant/orders/${d.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50" data-testid={`order-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[11px] text-gray-400">{d.id.slice(0, 8).toUpperCase()}</span>
                    <StatusPill status={d.status} />
                  </div>
                  <div className="mt-1.5 text-sm text-[#0a0a0a] truncate">{d.pickup.address} → {d.dropoff.address}</div>
                  <div className="text-xs text-gray-500">{d.driver_name ? `Driver · ${d.driver_name}` : "Awaiting assignment"}</div>
                </div>
                <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 ml-4">Open →</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
